// src/pages/Artists.js
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, updateDoc, doc } from "firebase/firestore";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import {
  FiSearch,
  FiFilter,
  FiUser,
  FiMapPin,
  FiImage,
  FiCreditCard,
  FiX,
  FiCheck,
} from "react-icons/fi";

const COLORS = {
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  card: "#ffffff",
  primary: "#f20c53",
  secondary: "#fb6c33",
  page: "#f5f6f8",
  ok: "#16a34a",
  warn: "#b91c1c",
};

const DEFAULT_PERMS = Object.freeze([]);

export default function Artists() {
  const [artists, setArtists] = useState([]);
  const [editStatus, setEditStatus] = useState({});
  const [toast, setToast] = useState(null); // {type,message}
  const [viewer, setViewer] = useState({ open: false, kind: "", data: null });
  const [hasPermission, setHasPermission] = useState(false);
  const [canVerify, setCanVerify] = useState(false);

  // UI controls
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All"); // All | Pending | Approved
  const [loading, setLoading] = useState(true);

  // User + perms (stable)
  const user = useMemo(() => {
    try {
      const raw = localStorage.getItem("employee");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);
  const userPermissions = useMemo(() => {
    const perms = user?.permissions;
    return Array.isArray(perms) ? perms : DEFAULT_PERMS;
  }, [user]);
  const isSuperAdmin = user?.role === "super_admin";

  // Permission & live data
  useEffect(() => {
    const canManage = isSuperAdmin || userPermissions.includes("manage_artists");
    const canVerifyLocal = isSuperAdmin || userPermissions.includes("verify_artist");
    setHasPermission(canManage);
    setCanVerify(canVerifyLocal);

    if (!canManage) {
      setLoading(false);
      return;
    }

    const unsub = onSnapshot(collection(db, "artists"), (snapshot) => {
      const list = snapshot.docs.map((d) => {
        const x = d.data() || {};
        return {
          docId: d.id,
          uid: x.uid || "—",
          stageName: x.stageName || x.name || "—",
          location: x.location || x.city || "—",
          artistCategories: Array.isArray(x.artistCategories) ? x.artistCategories : [],
          bank: x.bank || {},
          artistImageUrl: x.artistImageUrl || "",
          selfieImageUrl: x.selfieImageUrl || "",
          status: x.status || "Pending",
          isFeatured: !!x.isFeatured, // 🔹 NEW
          createdAt: x.createdAt || null,
        };
      });
      setArtists(sortArtists(list));
      setLoading(false);
    });

    return () => unsub();
  }, [isSuperAdmin, userPermissions]);

  // Derived list
  const filtered = useMemo(() => {
    let arr = [...artists];

    // search
    const q = search.trim().toLowerCase();
    if (q) {
      arr = arr.filter((a) => {
        const hay = [
          a.uid,
          a.stageName,
          a.location,
          (a.artistCategories || []).join(" "),
          a.status,
        ]
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    }

    // status filter
    if (statusFilter !== "All") {
      arr = arr.filter((a) => (a.status || "Pending") === statusFilter);
    }

    return arr;
  }, [artists, search, statusFilter]);

  const totals = useMemo(() => {
    const total = artists.length;
    const pending = artists.filter((a) => (a.status || "Pending") === "Pending").length;
    const approved = artists.filter((a) => (a.status || "Pending") === "Approved").length;
    return { total, pending, approved };
  }, [artists]);

  // Handlers
  function handleChange(docId, value) {
    setEditStatus((prev) => ({ ...prev, [docId]: value }));
  }

  async function handleUpdateStatus(docId) {
    if (!canVerify) {
      showToast("❌ You do not have permission to verify artists.", "error");
      return;
    }
    const newStatus = editStatus[docId];
    if (!newStatus) return;
    try {
      await updateDoc(doc(db, "artists", docId), { status: newStatus });
      showToast(`✅ Status updated to "${newStatus}"`, "success");
    } catch (error) {
      console.error("Update failed:", error);
      showToast("❌ Failed to update artist status.", "error");
    }
  }

  // 🔹 NEW: update isFeatured in Firestore when dropdown changes
  async function handleUpdateFeatured(docId, isFeatured) {
    if (!canVerify) {
      showToast("❌ You do not have permission to change featured status.", "error");
      return;
    }
    try {
      await updateDoc(doc(db, "artists", docId), { isFeatured });
      showToast(`✅ Featured set to ${isFeatured ? "true" : "false"}`, "success");
    } catch (error) {
      console.error("Update featured failed:", error);
      showToast("❌ Failed to update featured status.", "error");
    }
  }

  function showToast(message, type = "success") {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  }

  function openViewer(kind, data) {
    setViewer({ open: true, kind, data });
  }
  function closeViewer() {
    setViewer({ open: false, kind: "", data: null });
  }

  if (!hasPermission) {
    return (
      <div style={deniedOverlay}>
        <div style={deniedBox}>
          <FaExclamationCircle size={32} color="#e53935" />
          <h3 style={{ marginTop: "1rem" }}>Permission Denied</h3>
          <p style={{ color: "#666" }}>You do not have access to manage artists.</p>
          <button
            onClick={() => window.history.back()}
            style={ghostBtn}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="aa-pro-page" style={{ padding: 16, fontFamily: "Inter, system-ui, sans-serif", background: COLORS.page, minHeight: "100vh" }}>
      {/* Hero */}
      <div className="aa-pro-hero" style={hero}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="aa-pro-hero-icon" style={heroIcon}>
            <FiUser />
          </div>
          <div>
            <h2 className="aa-pro-title" style={heroTitle}>Artists</h2>
            <p className="aa-pro-subtitle" style={heroSub}>Search, review, and approve artist profiles.</p>
          </div>
        </div>
        <div className="aa-pro-metrics" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px,1fr))", gap: 8, minWidth: 280 }}>
          <Stat label="Total" value={totals.total} />
          <Stat label="Pending" value={totals.pending} tone="warn" />
          <Stat label="Approved" value={totals.approved} tone="ok" />
        </div>
      </div>

      {/* Controls */}
      <div className="aa-pro-toolbar" style={controlsRow}>
        <div style={searchBox}>
          <FiSearch style={{ opacity: 0.7 }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by UID, name, location, category…"
            style={searchInput}
          />
        </div>
        <div style={controlGroup}>
          <FiFilter />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={selectBare}
          >
            <option>All</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div
        className="aa-pro-table-shell"
        style={{
          overflowX: "auto",
          background: COLORS.card,
          borderRadius: 12,
          border: `1px solid ${COLORS.border}`,
          boxShadow: "0 6px 18px rgba(0,0,0,.06)",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 980 }}>
          <thead>
            <tr>
              <Th sticky>UID</Th>
              <Th sticky>Name</Th>
              <Th sticky>Location</Th>
              <Th sticky>Categories</Th>
              <Th sticky>Bank</Th>
              <Th sticky>Images</Th>
              <Th sticky>Featured</Th>{/* 🔹 NEW */}
              <Th sticky>Status</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} style={tdMuted}>Loading…</td> {/* 🔹 updated colspan */}
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={8} style={tdMuted}>No records found.</td> {/* 🔹 updated colspan */}
              </tr>
            ) : (
              filtered.map((a, i) => {
                const rowBg = i % 2 ? "#fafafa" : "#fff";
                return (
                  <tr
                    key={a.docId}
                    style={{
                      borderTop: `1px solid ${COLORS.border}`,
                      background: rowBg,
                      transition: "background .15s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = rowBg)}
                  >
                    <Td mono title={a.uid}>
                      {shorten(a.uid, 10)}
                    </Td>
                    <Td>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <FiUser /> {a.stageName}
                      </div>
                    </Td>
                    <Td>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <FiMapPin /> {a.location}
                      </div>
                    </Td>
                    <Td>
                      <button
                        onClick={() => openViewer("Categories", a.artistCategories)}
                        style={linkBtn}
                        title="View categories"
                      >
                        View
                      </button>
                    </Td>
                    <Td>
                      <button
                        onClick={() => openViewer("Bank", a.bank)}
                        style={linkBtn}
                        title="View bank details"
                      >
                        View
                      </button>
                    </Td>
                    <Td>
                      <button
                        onClick={() =>
                          openViewer("Images", {
                            artistImageUrl: a.artistImageUrl,
                            selfieImageUrl: a.selfieImageUrl,
                          })
                        }
                        style={linkBtn}
                        title="View images"
                      >
                        View
                      </button>
                    </Td>
                    {/* 🔹 NEW Featured column cell */}
                    <Td>
                      <select
                        value={a.isFeatured ? "true" : "false"}
                        onChange={(e) =>
                          handleUpdateFeatured(a.docId, e.target.value === "true")
                        }
                        style={selectStyle}
                        disabled={!canVerify}
                        title={canVerify ? "Toggle featured" : "No permission"}
                      >
                        <option value="false">False</option>
                        <option value="true">True</option>
                      </select>
                    </Td>
                    <Td>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <StatusChip status={a.status || "Pending"} />
                        <select
                          value={editStatus[a.docId] || a.status || "Pending"}
                          onChange={(e) => handleChange(a.docId, e.target.value)}
                          style={selectStyle}
                          disabled={!canVerify}
                          title={canVerify ? "Change status" : "No permission"}
                        >
                          <option value="Pending">Pending</option>
                          <option value="Approved">Approved</option>
                        </select>
                        <button
                          onClick={() => handleUpdateStatus(a.docId)}
                          style={{ ...primaryBtn, opacity: canVerify ? 1 : 0.7 }}
                          disabled={!canVerify}
                          title={canVerify ? "Update status" : "No permission"}
                        >
                          <FiCheck /> Update
                        </button>
                      </div>
                    </Td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Viewer Modal */}
      {viewer.open && (
        <div style={modalBackdrop} onClick={closeViewer} role="dialog" aria-modal="true">
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ fontWeight: 900, color: COLORS.text }}>{viewer.kind} Details</div>
              <button onClick={closeViewer} style={iconBtn} aria-label="Close">
                <FiX />
              </button>
            </div>

            <div style={{ display: "grid", gap: 10 }}>
              {viewer.kind === "Bank" && viewer.data ? (
                <div style={cardInner}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, color: COLORS.subtext }}>
                    <FiCreditCard /> Bank Info
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <tbody>
                      {Object.entries(viewer.data).map(([k, v]) => (
                        <tr key={k}>
                          <td style={{ ...cellKey }}>{k}</td>
                          <td style={{ ...cellVal }}>{String(v ?? "—")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : viewer.kind === "Categories" && Array.isArray(viewer.data) ? (
                <div style={cardInner}>
                  <div style={{ color: COLORS.subtext, marginBottom: 6 }}>Categories</div>
                  {viewer.data.length ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {viewer.data.map((c, idx) => (
                        <span key={idx} style={pill}>
                          {c}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: COLORS.subtext }}>No categories found.</div>
                  )}
                </div>
              ) : viewer.kind === "Images" ? (
                <div style={{ display: "grid", gap: 12 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <ImageCard title="Artist Photo" url={viewer.data?.artistImageUrl} />
                    <ImageCard title="Selfie" url={viewer.data?.selfieImageUrl} />
                  </div>
                </div>
              ) : (
                <div style={{ color: COLORS.subtext }}>No data found.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 16,
            right: 16,
            background: toast.type === "error" ? "#fef2f2" : "#ecfdf5",
            border: `1px solid ${toast.type === "error" ? "#fecaca" : "#86efac"}`,
            borderRadius: 10,
            padding: "10px 12px",
            boxShadow: "0 8px 24px rgba(0,0,0,.12)",
            color: COLORS.text,
            fontWeight: 800,
            zIndex: 90,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {toast.type === "error" ? <FaExclamationCircle /> : <FaCheckCircle />}
          {toast.message}
        </div>
      )}
    </div>
  );
}

/* ---------- Parts ---------- */
function Th({ children, sticky }) {
  return (
    <th
      style={{
        textAlign: "left",
        padding: 10,
        fontWeight: 800,
        color: COLORS.text,
        background: "#f8fafc",
        position: sticky ? "sticky" : "static",
        top: 0,
        zIndex: 5,
        borderBottom: `1px solid ${COLORS.border}`,
      }}
    >
      {children}
    </th>
  );
}
function Td({ children, mono, title }) {
  return (
    <td
      title={title}
      style={{
        padding: 10,
        color: COLORS.text,
        verticalAlign: "top",
        fontFamily: mono ? "ui-monospace, SFMono-Regular, Menlo, monospace" : "inherit",
      }}
    >
      {children}
    </td>
  );
}

function StatusChip({ status }) {
  const s = String(status || "Pending");
  const map = {
    Pending: { bg: "#fff7ed", fg: "#b45309", bd: "#fed7aa" },
    Approved: { bg: "#ecfdf5", fg: "#065f46", bd: "#a7f3d0" },
  };
  const v = map[s] || map.Pending;
  return (
    <span
      style={{
        padding: "4px 10px",
        borderRadius: 999,
        background: v.bg,
        color: v.fg,
        fontWeight: 800,
        border: `1px solid ${v.bd}`,
      }}
    >
      {s}
    </span>
  );
}

function ImageCard({ title, url }) {
  return (
    <div style={cardInner}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, color: COLORS.subtext }}>
        <FiImage /> {title}
      </div>
      {url ? (
        <div style={imgBox}>
          <img src={url} alt={title} style={imgStyle} />
        </div>
      ) : (
        <div style={{ color: COLORS.subtext }}>No image</div>
      )}
    </div>
  );
}

/* ---------- Helpers ---------- */
function sortArtists(list) {
  return [...list].sort((a, b) => {
    // Pending first, then Approved; inside same status, no date guaranteed—fall back to stageName
    const rank = (s) => (s === "Pending" ? 0 : s === "Approved" ? 1 : 2);
    const ar = rank(a.status || "Pending");
    const br = rank(b.status || "Pending");
    if (ar !== br) return ar - br;
    return String(a.stageName || "").localeCompare(String(b.stageName || ""));
  });
}
function shorten(s, n = 8) {
  if (!s) return "—";
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

/* ---------- Styles ---------- */
const hero = {
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  color: "#fff",
  padding: "14px 16px",
  borderRadius: 14,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  boxShadow: "0 14px 26px rgba(0,0,0,0.12)",
  marginBottom: 12,
};
const heroIcon = {
  width: 42,
  height: 42,
  borderRadius: 10,
  display: "grid",
  placeItems: "center",
  background: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(255,255,255,0.35)",
};
const heroTitle = { margin: 0, fontSize: 20, fontWeight: 900, lineHeight: 1.1 };
const heroSub = { margin: "4px 0 0", opacity: 0.92, fontSize: 13 };

function Stat({ label, value, tone }) {
  const map =
    tone === "ok"
      ? { bg: "#ecfdf5", bd: "#a7f3d0", fg: "#065f46" }
      : tone === "warn"
      ? { bg: "#fff7ed", bd: "#fed7aa", fg: "#b45309" }
      : { bg: "#fff", bd: COLORS.border, fg: COLORS.text };
  return (
    <div
      className="aa-pro-metric"
      style={{
        background: map.bg,
        border: `1px solid ${map.bd}`,
        borderRadius: 10,
        padding: "10px 12px",
        minWidth: 120,
        display: "grid",
        gap: 2,
      }}
    >
      <div style={{ fontSize: 12, color: COLORS.subtext }}>{label}</div>
      <div style={{ fontWeight: 900, color: map.fg, fontSize: 18 }}>{value}</div>
    </div>
  );
}

const controlsRow = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
  marginBottom: 12,
};

const searchBox = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: "10px 12px",
  background: "#fff",
  minWidth: 260,
  flex: 1,
};
const searchInput = { border: "none", outline: "none", flex: 1, fontSize: 14 };

const controlGroup = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: "8px 10px",
  background: "#fff",
};
const selectBare = {
  border: "none",
  outline: "none",
  background: "transparent",
  fontSize: 14,
  paddingRight: 4,
  cursor: "pointer",
};

const tdMuted = { padding: 12, color: COLORS.subtext };

const linkBtn = {
  border: "none",
  background: "#fff",
  color: COLORS.primary,
  fontWeight: 800,
  cursor: "pointer",
  padding: "6px 10px",
  borderRadius: 8,
  borderColor: COLORS.border,
  borderWidth: 1,
  borderStyle: "solid",
};

const selectStyle = {
  height: 34,
  padding: "0 10px",
  borderRadius: 8,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
};

const primaryBtn = {
  height: 34,
  padding: "0 14px",
  borderRadius: 8,
  border: "none",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const deniedOverlay = {
  backdropFilter: "blur(6px)",
  backgroundColor: "rgba(0, 0, 0, 0.2)",
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 999,
};
const deniedBox = {
  background: "#fff",
  padding: "2rem",
  borderRadius: "12px",
  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
  textAlign: "center",
};

const ghostBtn = {
  marginTop: "1.2rem",
  background: "#555",
  color: "#fff",
  padding: "0.6rem 1.2rem",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontWeight: 700,
};

const modalBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.45)",
  display: "grid",
  placeItems: "center",
  zIndex: 60,
  padding: 16,
};
const modalCard = {
  width: "100%",
  maxWidth: 780,
  background: "#fff",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 14,
  padding: 14,
  boxShadow: "0 10px 30px rgba(0,0,0,.25)",
};
const iconBtn = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  width: 34,
  height: 34,
  borderRadius: 8,
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
};

const cardInner = {
  background: "#fff",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: 10,
};
const cellKey = { fontWeight: 800, padding: "6px 8px", width: "40%", borderBottom: `1px solid ${COLORS.border}` };
const cellVal = { padding: "6px 8px", borderBottom: `1px solid ${COLORS.border}` };

const imgBox = {
  display: "grid",
  placeItems: "center",
  maxHeight: "60vh",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 8,
  overflow: "hidden",
};
const imgStyle = {
  width: "100%",
  height: "auto",
  objectFit: "cover",
  maxHeight: "60vh",
};

const pill = {
  padding: "4px 8px",
  background: "#f1f5f9",
  borderRadius: 999,
  border: `1px solid ${COLORS.border}`,
  fontSize: 12,
  fontWeight: 800,
  color: COLORS.text,
};
